import { isTrackingDisabledOnCurrentPath } from './trackingDisabledPaths'

const ZENDESK_SNIPPET_URL =
  'https://static.zdassets.com/ekr/snippet.js?key=c226ac7a-ad52-42d9-9510-8ea9d2bdc5e6'

let zendeskLoadPromise: Promise<void> | null = null
let pendingScript: HTMLScriptElement | null = null
let pendingLoadResolve: (() => void) | null = null
let widgetShouldBeVisible = false

function cancelPendingZendeskLoad(): void {
  if (pendingScript) {
    pendingScript.onload = null
    pendingScript.onerror = null
    pendingScript.remove()
    pendingScript = null
  }

  pendingLoadResolve?.()
  pendingLoadResolve = null
  zendeskLoadPromise = null
}

function configureZendeskWidgetSettings(): void {
  window.zESettings = {
    webWidget: {
      contactForm: {
        ticketForms: [{ id: 15673612216343 }],
      },
    },
  }
}

function hideZendeskWidget(): void {
  try {
    window.zE?.('webWidget', 'hide')
  } catch (error) {
    console.error(error)
  }
}

function showZendeskWidget(): void {
  try {
    window.zE?.('webWidget', 'show')
  } catch (error) {
    console.error(error)
  }
}

function applyZendeskWidgetVisibility(): void {
  if (widgetShouldBeVisible) {
    showZendeskWidget()
  } else {
    hideZendeskWidget()
  }
}

export function showZendeskWidgetIfAllowed(): Promise<void> {
  if (isTrackingDisabledOnCurrentPath()) {
    return Promise.resolve()
  }

  widgetShouldBeVisible = true

  if (window.zE && document.getElementById('ze-snippet')) {
    showZendeskWidget()
    return Promise.resolve()
  }

  return loadZendeskWidgetIfAllowed().then(() => {
    if (widgetShouldBeVisible && !isTrackingDisabledOnCurrentPath()) {
      showZendeskWidget()
    }
  })
}

export function hideZendeskWidgetIfAllowed(): void {
  widgetShouldBeVisible = false
  hideZendeskWidget()
}

export function loadZendeskWidgetIfAllowed(): Promise<void> {
  if (isTrackingDisabledOnCurrentPath()) {
    cancelPendingZendeskLoad()
    hideZendeskWidget()
    return Promise.resolve()
  }

  if (zendeskLoadPromise) {
    return zendeskLoadPromise
  }

  const existingScript = document.getElementById('ze-snippet')
  if (existingScript) {
    if (window.zE) {
      return Promise.resolve()
    }

    existingScript.remove()
  }

  configureZendeskWidgetSettings()

  zendeskLoadPromise = new Promise((resolve, reject) => {
    pendingLoadResolve = resolve
    const script = document.createElement('script')
    pendingScript = script
    script.id = 'ze-snippet'
    script.src = ZENDESK_SNIPPET_URL
    script.async = true
    script.onload = () => {
      pendingScript = null
      pendingLoadResolve = null

      if (isTrackingDisabledOnCurrentPath()) {
        script.remove()
        zendeskLoadPromise = null
        hideZendeskWidget()
        resolve()
        return
      }

      applyZendeskWidgetVisibility()
      resolve()
    }
    script.onerror = () => {
      script.onload = null
      script.onerror = null
      script.remove()
      pendingScript = null
      pendingLoadResolve = null
      zendeskLoadPromise = null
      reject(new Error('Failed to load Zendesk widget'))
    }
    document.body.appendChild(script)
  })

  return zendeskLoadPromise
}
